import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogsAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import ConfirmModal from '../components/ConfirmModal';
import toast from 'react-hot-toast';

const ManageBlogs: React.FC = () => {
  const [blogs, setBlogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, [filter]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = filter ? { status: filter } : {};
      const response = await blogsAPI.getAllAdmin(params);
      setBlogs(response.data.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Publish Blog',
      message: 'Are you sure you want to publish this blog? It will be visible to all users.',
      type: 'success',
      confirmText: 'Publish',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await blogsAPI.publish(id);
          toast.success('Blog published');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchBlogs();
        } catch (error) {
          toast.error('Failed to publish blog');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const handleToggleTrending = (id: string, currentTrending: boolean) => {
    setConfirmModal({
      isOpen: true,
      title: currentTrending ? 'Remove from Trending' : 'Add to Trending',
      message: `Are you sure you want to ${currentTrending ? 'remove from' : 'add to'} trending?`,
      type: 'info',
      confirmText: currentTrending ? 'Remove' : 'Add',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await blogsAPI.toggleTrending(id);
          toast.success(currentTrending ? 'Removed from trending' : 'Added to trending');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchBlogs();
        } catch (error) {
          toast.error('Failed to update trending');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Blog',
      message: 'Are you sure you want to delete this blog? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        setProcessing(true);
        try {
          await blogsAPI.delete(id);
          toast.success('Blog deleted');
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchBlogs();
        } catch (error) {
          toast.error('Failed to delete blog');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Blogs</h1>
            <p className="text-gray-600 mt-1">Create and manage blog posts</p>
          </div>
          <Link
            to="/admin/blogs/create"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Create Blog
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Blogs</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : blogs.length > 0 ? (
          <div className="space-y-4">
            {blogs.map((blog) => (
              <div key={blog._id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{blog.title}</h3>
                      {blog.isTrending && (
                        <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded">
                          🔥 Trending
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">{blog.excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className={`px-3 py-1 rounded-full ${
                        blog.status === 'published' ? 'bg-green-100 text-green-600' :
                        blog.status === 'draft' ? 'bg-yellow-100 text-yellow-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {blog.status}
                      </span>
                      <span>{blog.category}</span>
                      <span>{blog.views} views</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    <Link
                      to={`/admin/blogs/edit/${blog._id}`}
                      className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Edit
                    </Link>
                    {blog.status === 'draft' && (
                      <button
                        onClick={() => handlePublish(blog._id)}
                        className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        Publish
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleTrending(blog._id, blog.isTrending)}
                      className="px-4 py-2 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                    >
                      {blog.isTrending ? 'Remove Trending' : 'Mark Trending'}
                    </button>
                    <button
                      onClick={() => handleDelete(blog._id)}
                      className="px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">No blogs found</p>
            <Link
              to="/admin/blogs/create"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create First Blog
            </Link>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        loading={processing}
      />
    </AdminLayout>
  );
};

export default ManageBlogs;
